<template>
    <div class="password-recovery" :class="'is-' + step">
        <transition name="step" mode="out-in">
            <div :key="step" class="step">
                <img v-if="step !== 'success' && step !== 'blocked'" class="logo" src="images/icons/logo-mark.svg" alt="" />
                <img v-else-if="step === 'success'" class="status-icon" src="images/icons/check-circle.svg" alt="" />
                <span v-else class="alert-badge" aria-hidden="true"><img src="images/icons/alert-circle-red.svg" alt="" /></span>

                <h1 class="title" :class="{ 'title--dark': step === 'blocked' }" v-html="title"></h1>
                <p v-if="step === 'code'" class="subtitle">
                    {{ t('login-recovery-code_subtitle', { 0: masked }) }}<br>{{ t('login-recovery-code_valid') }}
                </p>
                <p v-else class="subtitle">{{ subtitle }}</p>

                <form v-if="step === 'identifier'" class="stack" @submit.prevent="start">
                    <label class="field">
                        <span>{{ t('login-recovery-identifier_label') }}</span>
                        <input id="recovery-identifier" v-model="identifier" type="text"
                            :placeholder="t('login-recovery-identifier_placeholder')" autocomplete="username" />
                    </label>
                    <p class="error" v-if="error" role="alert">{{ error }}</p>
                    <button class="primary" type="submit" :disabled="loading || !identifier.trim()">
                        {{ t('login-recovery-continue') }}
                    </button>
                    <button type="button" class="back-link" @click="$emit('close')">
                        <img src="images/icons/arrow-left-gray.svg" alt="" />
                        {{ t('login-recovery-back_to_login') }}
                    </button>
                </form>

                <div v-else-if="step === 'channel'" class="stack">
                    <button v-for="channel in channels" :key="channel.id" type="button" class="channel" :disabled="loading" @click="send(channel.id)">
                        <span class="channel-icon" aria-hidden="true">
                            <img v-if="channel.id === 'sms'" src="images/icons/message-square.svg" alt="" />
                            <img v-else src="images/icons/mail-02.svg" alt="" />
                        </span>
                        <span class="channel-copy">
                            <strong>{{ channel.id === 'sms' ? t('login-recovery-sms') : t('login-recovery-email') }}</strong>
                            <small>{{ channel.masked }}</small>
                        </span>
                        <img class="chevron" src="images/icons/chevron-right.svg" alt="" />
                    </button>
                    <p class="error" v-if="error" role="alert">{{ error }}</p>
                    <button type="button" class="back-link" @click="backToIdentifier">
                        <img src="images/icons/arrow-left-gray.svg" alt="" />
                        {{ t('login-recovery-change_identifier') }}
                    </button>
                </div>

                <form v-else-if="step === 'code'" class="stack stack--code" @submit.prevent="verify">
                    <div class="code-inputs">
                        <input v-for="index in 6" :key="index" maxlength="1" class="code-input" inputmode="numeric"
                            placeholder="0" :value="digits[index - 1]" @input="onDigit($event, index - 1)"
                            @keydown="onKey($event, index - 1)" @paste="onPaste" />
                    </div>
                    <p class="error" v-if="error" role="alert">{{ error }}</p>
                    <p class="info" v-else-if="info" role="status">{{ info }}</p>
                    <button class="primary" type="submit" :disabled="loading || code.length < 6">
                        {{ t('login-recovery-verify') }}
                    </button>
                    <p class="resend">
                        {{ t('login-recovery-resend_prefix') }}
                        <span v-if="cooldown > 0" class="resend-wait">{{ t('login-recovery-resend_in', { 0: cooldownLabel }) }}</span>
                        <button v-else type="button" :disabled="loading" @click="resend">{{ t('login-recovery-resend_action') }}</button>
                    </p>
                    <button type="button" class="back-link" @click="backToIdentifier">
                        <img src="images/icons/arrow-left-gray.svg" alt="" />
                        {{ t('login-recovery-change_identifier') }}
                    </button>
                </form>

                <form v-else-if="step === 'password'" class="stack" @submit.prevent="complete">
                    <label class="field">
                        <span>{{ t('login-recovery-new_password') }}</span>
                        <input v-model="password" type="password" autocomplete="new-password" placeholder="**********************" />
                    </label>
                    <label class="field">
                        <span>{{ t('login-recovery-repeat_password') }}</span>
                        <input v-model="passwordConfirmation" type="password" autocomplete="new-password" placeholder="**********************" />
                    </label>
                    <p class="error" v-if="error" role="alert">{{ error }}</p>
                    <button class="primary" type="submit" :disabled="loading">
                        {{ t('login-recovery-save') }}
                    </button>
                    <button type="button" class="back-link" @click="$emit('close')">
                        <img src="images/icons/arrow-left-gray.svg" alt="" />
                        {{ t('login-recovery-back_to_login') }}
                    </button>
                </form>

                <div v-else class="stack">
                    <button class="primary" type="button" @click="$emit('close')">
                        <img v-if="step === 'blocked'" src="images/icons/arrow-left.svg" alt="" />
                        {{ step === 'blocked' ? t('login-recovery-blocked_back') : t('login-recovery-back_to_login') }}
                    </button>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import keviApi from "../keviApi";

const RESEND_COOLDOWN_SECONDS = 60;

export default {
    name: "password-recovery",
    props: {
        initialIdentifier: { type: String, default: "" },
    },
    data() {
        return {
            step: "identifier",
            identifier: this.initialIdentifier || "",
            channels: [],
            sentChannel: "",
            masked: "",
            digits: ["", "", "", "", "", ""],
            password: "",
            passwordConfirmation: "",
            resetToken: "",
            loading: false,
            error: "",
            info: "",
            cooldown: 0,
            cooldownTimer: null,
        };
    },
    beforeDestroy() {
        this.stopCooldown();
    },
    computed: {
        cooldownLabel() {
            const minutes = Math.floor(this.cooldown / 60);
            const seconds = String(this.cooldown % 60).padStart(2, "0");
            return `${minutes}:${seconds}`;
        },
        code() {
            return this.digits.join("");
        },
        title() {
            const map = {
                identifier: "login-recovery-title",
                channel: "login-recovery-choose_title",
                code: "login-recovery-code_title",
                password: "login-recovery-password_title",
                success: "login-recovery-success_title",
                blocked: "login-recovery-blocked_title",
            };
            return this.t(map[this.step]);
        },
        subtitle() {
            if (this.step === "identifier") return this.t("login-recovery-subtitle");
            if (this.step === "channel") return this.t("login-recovery-choose_subtitle");
            if (this.step === "password") return this.t("login-recovery-password_hint");
            if (this.step === "success") return this.t("login-recovery-success_subtitle");
            if (this.step === "blocked") return this.t("login-recovery-blocked_subtitle");
            return "";
        },
    },
    methods: {
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
        message(code) {
            const map = {
                invalid_code: "login-recovery-invalid_code",
                expired: "login-recovery-expired",
                too_many_attempts: "login-recovery-too_many_attempts",
                mismatch: "login-recovery-mismatch",
                weak_password: "login-recovery-weak",
                too_many: "login-recovery-too_many",
                send_failed: "login-recovery-send_failed",
            };
            return this.t(map[code] || "login-recovery-send_failed");
        },
        startCooldown() {
            this.stopCooldown();
            this.cooldown = RESEND_COOLDOWN_SECONDS;
            this.cooldownTimer = window.setInterval(() => {
                this.cooldown = Math.max(0, this.cooldown - 1);
                if (this.cooldown === 0) this.stopCooldown();
            }, 1000);
        },
        stopCooldown() {
            if (this.cooldownTimer) {
                window.clearInterval(this.cooldownTimer);
                this.cooldownTimer = null;
            }
        },
        backToIdentifier() {
            this.stopCooldown();
            this.cooldown = 0;
            this.error = "";
            this.info = "";
            this.channels = [];
            this.step = "identifier";
        },
        resend() {
            this.send(this.sentChannel, true);
        },
        start() {
            if (!this.identifier.trim() || this.loading) return;
            this.loading = true;
            this.error = "";
            const options = keviApi.getOptions("auth/password-recovery/start", "POST", {
                identifier: this.identifier.trim(),
            });
            this.$http(options).then(
                (response) => {
                    this.loading = false;
                    this.applyStart(response.body);
                },
                (response) => {
                    this.loading = false;
                    this.error = this.message(response.body && response.body.message);
                }
            );
        },
        applyStart(body, resent) {
            if (!body || body.status === "no_channel") {
                this.step = "blocked";
                return;
            }
            if (body.status === "choose_channel") {
                this.channels = body.channels || [];
                this.step = "channel";
                return;
            }
            if (body.status === "code_sent") {
                this.info = resent ? this.t("login-recovery-resent") : "";
                this.startCooldown();
                this.sentChannel = body.channel;
                this.masked = body.masked;
                this.digits = ["", "", "", "", "", ""];
                if (body.debug_code) {
                    const digits = String(body.debug_code).replace(/\D/g, "").slice(0, 6).split("");
                    this.digits = [0, 1, 2, 3, 4, 5].map((i) => digits[i] || "");
                    this.error = `Código de desenvolvimento: ${body.debug_code}`;
                }
                this.step = "code";
                this.$nextTick(() => {
                    const input = this.$el.querySelector(".code-input");
                    if (input) input.focus();
                });
            }
        },
        send(channel, resent) {
            this.loading = true;
            this.error = "";
            this.info = "";
            const options = keviApi.getOptions("auth/password-recovery/send", "POST", {
                identifier: this.identifier.trim(),
                channel,
            });
            this.$http(options).then(
                (response) => {
                    this.loading = false;
                    this.applyStart(response.body, resent);
                },
                (response) => {
                    this.loading = false;
                    this.error = this.message(response.body && response.body.message);
                }
            );
        },
        verify() {
            if (this.code.length < 6 || this.loading) return;
            this.loading = true;
            this.error = "";
            this.info = "";
            const options = keviApi.getOptions("auth/password-recovery/verify", "POST", {
                identifier: this.identifier.trim(),
                code: this.code,
            });
            this.$http(options).then(
                (response) => {
                    this.loading = false;
                    if (response.body && response.body.reset_token) {
                        this.resetToken = response.body.reset_token;
                        this.step = "password";
                    } else {
                        this.error = this.t("login-recovery-invalid_code");
                    }
                },
                (response) => {
                    this.loading = false;
                    const code = response.body && response.body.message;
                    if (code === "too_many_attempts" || code === "expired") {
                        this.digits = ["", "", "", "", "", ""];
                        this.stopCooldown();
                        this.cooldown = 0;
                    }
                    this.error = response.status === 400 || response.status === 429
                        ? this.message(code || "invalid_code")
                        : this.t("login-recovery-network");
                }
            );
        },
        complete() {
            if (this.loading) return;
            if (this.password.length < 8) {
                this.error = this.t("login-recovery-weak");
                return;
            }
            if (this.password !== this.passwordConfirmation) {
                this.error = this.t("login-recovery-mismatch");
                return;
            }
            this.loading = true;
            this.error = "";
            const options = keviApi.getOptions("auth/password-recovery/complete", "POST", {
                reset_token: this.resetToken,
                password: this.password,
                password_confirmation: this.passwordConfirmation,
            });
            this.$http(options).then(
                () => {
                    this.loading = false;
                    this.step = "success";
                },
                (response) => {
                    this.loading = false;
                    this.error = this.message(response.body && response.body.message);
                }
            );
        },
        onDigit(event, index) {
            const value = (event.target.value || "").replace(/\D/g, "").slice(-1);
            this.$set(this.digits, index, value);
            event.target.value = value;
            if (value && event.target.nextElementSibling) {
                event.target.nextElementSibling.focus();
            }
        },
        onKey(event, index) {
            if (event.key === "Backspace" && !this.digits[index] && event.target.previousElementSibling) {
                event.target.previousElementSibling.focus();
            }
        },
        onPaste(event) {
            event.preventDefault();
            const text = (event.clipboardData || window.clipboardData).getData("text").replace(/\D/g, "").slice(0, 6);
            this.digits = [0, 1, 2, 3, 4, 5].map((index) => text[index] || "");
        },
    },
};
</script>

<style scoped>
.password-recovery { width: 100%; display: flex; justify-content: center; }
.step {
    width: 333px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
}
.is-code .step { width: 477px; }
.is-blocked .step { width: 523px; }
.logo { width: 32px; height: 40px; display: block; }
.status-icon { width: 48px; height: 48px; display: block; }
.alert-badge {
    width: 40px; height: 40px; border-radius: 100px; background: #fef3f2;
    display: flex; align-items: center; justify-content: center; padding: 8px; box-sizing: border-box;
}
.alert-badge img { width: 24px; height: 24px; display: block; }
.title, .subtitle, .field span, .channel-copy strong, .channel-copy small {
    text-box-trim: trim-both;
    text-box-edge: cap alphabetic;
}
.title {
    margin: 40px 0 0;
    color: #2aaa4c;
    font-family: "Sora", sans-serif;
    font-size: 32px;
    font-weight: 500;
    line-height: 1.2;
}
.title--dark { color: #383c50; }
.subtitle {
    margin: 16px 0 0;
    width: 304px;
    color: #656565;
    font-family: "Sora", sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.3;
}
.is-channel .subtitle, .is-code .subtitle, .is-password .subtitle, .is-success .subtitle, .is-blocked .subtitle {
    font-family: "Inter", sans-serif;
    line-height: 1.2;
}
.is-code .subtitle { width: 477px; }
.is-blocked .subtitle { width: 359px; margin-top: 24px; }
.stack {
    width: 100%;
    margin-top: 48px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;
}
.is-channel .stack { gap: 16px; }
.is-code .stack { margin-top: 40px; }
.is-blocked .stack { margin-top: 24px; }
.field { width: 330px; display: flex; flex-direction: column; align-items: flex-start; gap: 8px; text-align: left; margin: 0; }
.field span { color: #383c50; font-family: "Inter", sans-serif; font-size: 14px; font-weight: 500; line-height: 1.2; }
.field input {
    box-sizing: border-box; width: 100%; height: 44px; margin: 0; padding: 0 12px;
    border-radius: 8px; border: 1px solid #e5e6eb; background: #f9f9fb;
    color: #1f2337; font-family: "Inter", sans-serif; font-size: 12px; font-weight: 400; line-height: 1.2;
    box-shadow: none;
}
.field input::placeholder { color: #9598a6; font-size: 12px; }
.field input:focus { outline: none; border-color: #bbbdc7; background: #fff; }
.primary {
    width: 330px; box-sizing: border-box; padding: 12px; border: 0; border-radius: 8px;
    background: #2aaa4c; color: #f1fcf3; font-family: "Inter", sans-serif;
    font-size: 16px; font-weight: 400; line-height: 1.2; cursor: pointer;
    display: inline-flex; align-items: center; justify-content: center; gap: 4px;
    text-align: center;
}
.primary img { width: 20px; height: 20px; display: block; }
.is-code .primary { width: 395px; }
.primary:disabled { cursor: default; }
.back-link {
    margin: 0;
    padding: 0;
    border: 0;
    background: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    color: #626473;
    font-family: "Inter", sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.2;
    cursor: pointer;
}
.back-link img { width: 16px; height: 16px; display: block; }
.back-link:hover { color: #383c50; }
.channel {
    width: 100%; box-sizing: border-box; display: flex; align-items: center; justify-content: space-between;
    padding: 16px; border: 1px solid #f3f3f6; border-radius: 16px; background: #fafafc; cursor: pointer; text-align: left;
}
.channel-icon {
    width: 40px; height: 40px; border-radius: 100px; background: rgba(0, 0, 0, 0.05);
    display: flex; align-items: center; justify-content: center; flex: none; padding: 8px; box-sizing: border-box;
}
.channel-icon img { width: 24px; height: 24px; display: block; }
.channel-copy { display: flex; flex-direction: column; gap: 16px; flex: 1; margin-left: 16px; min-width: 0; }
.channel-copy strong { color: #383c50; font-family: "Sora", sans-serif; font-size: 16px; font-weight: 500; line-height: 1.3; }
.channel-copy small { color: #626473; font-family: "Inter", sans-serif; font-size: 14px; font-weight: 400; line-height: 1.2; }
.chevron { width: 24px; height: 24px; flex: none; display: block; }
.code-inputs { display: flex; gap: 8px; }
.code-input {
    box-sizing: border-box; width: 59px; height: 66px; padding: 0; border-radius: 8px;
    border: 1px solid #bdbdbd; background: #f8f8f8; text-align: center;
    font-family: "Sora", sans-serif; font-size: 14px; font-weight: 400; line-height: 1.3; color: #1f2337;
}
.code-input::placeholder { color: #bdbdbd; }
.code-input:focus { outline: none; border-color: #2aaa4c; background: #fff; }
.resend { margin: 0; width: 100%; color: #656565; font-family: "Sora", sans-serif; font-size: 14px; font-weight: 400; line-height: 1.3; }
.resend button { border: 0; background: none; padding: 0; color: #2aaa4c; font: inherit; text-decoration: underline; cursor: pointer; }
.resend button:disabled { cursor: default; opacity: .6; }
.resend-wait { color: #9598a6; }
.info { margin: 0; width: 330px; color: #209941; font-family: "Inter", sans-serif; font-size: 13px; text-align: left; }
.error { margin: 0; width: 330px; color: #d92d20; font-family: "Inter", sans-serif; font-size: 13px; text-align: left; }
@media (max-width: 600px) {
    .step, .is-code .step, .is-blocked .step { width: 100%; padding: 0 16px; box-sizing: border-box; }
    .subtitle, .is-code .subtitle, .is-blocked .subtitle { width: 100%; }
    .field, .primary, .is-code .primary, .error, .info { width: 100%; }
    .code-inputs { gap: 6px; }
    .code-input { width: 44px; height: 56px; }
}
.step-enter-active, .step-leave-active { transition: opacity .2s ease, transform .24s cubic-bezier(.16, 1, .3, 1); }
.step-enter { opacity: 0; transform: translateY(10px); }
.step-leave-to { opacity: 0; transform: translateY(-8px); }
</style>
